import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

export interface AuthorizedUser {
  id?: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: 'super_admin' | 'admin' | 'user';
  isActive: boolean;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  createdBy?: string;
  lastLoginAt?: Timestamp | Date | null;
}

export interface ActivityLog {
  id?: string;
  userEmail: string;
  action: string;
  details: any;
  createdAt: Timestamp | Date;
}

// Get all authorized users
export const getAuthorizedUsers = async (): Promise<AuthorizedUser[]> => {
  try {
    const usersQuery = query(
      collection(db, 'authorizedUsers'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(usersQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AuthorizedUser));
  } catch (error) {
    console.error('Error fetching authorized users:', error);
    throw error;
  }
};

// Get current user's authorization info
export const getCurrentUserAuth = async (): Promise<AuthorizedUser | null> => {
  try {
    const user = auth.currentUser;
    if (!user?.email) return null;

    const userDoc = await getDoc(doc(db, 'authorizedUsers', user.email));
    if (!userDoc.exists()) return null;

    return {
      id: userDoc.id,
      ...userDoc.data()
    } as AuthorizedUser;
  } catch (error) {
    console.error('Error fetching current user auth:', error);
    throw error;
  }
};

// Create new authorized user
export const createAuthorizedUser = async (userData: Omit<AuthorizedUser, 'id' | 'createdAt' | 'createdBy'>): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user?.email) throw new Error('Not authenticated');

    const userDocRef = doc(db, 'authorizedUsers', userData.email);
    await setDoc(userDocRef, {
      ...userData,
      createdAt: serverTimestamp(),
      createdBy: user.email
    });

    // Log activity
    await logActivity('CREATE_USER', {
      targetEmail: userData.email,
      role: userData.role
    });
  } catch (error) {
    console.error('Error creating authorized user:', error);
    throw error;
  }
};

// Update authorized user
export const updateAuthorizedUser = async (
  email: string,
  updates: Partial<Omit<AuthorizedUser, 'id' | 'email' | 'createdAt' | 'createdBy'>>
): Promise<void> => {
  try {
    const userDocRef = doc(db, 'authorizedUsers', email);
    await updateDoc(userDocRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    // Log activity
    await logActivity('UPDATE_USER', {
      targetEmail: email,
      changes: updates
    });
  } catch (error) {
    console.error('Error updating authorized user:', error);
    throw error;
  }
};

// Delete authorized user
export const deleteAuthorizedUser = async (email: string): Promise<void> => {
  try {
    const userDocRef = doc(db, 'authorizedUsers', email);
    await deleteDoc(userDocRef);

    // Log activity
    await logActivity('DELETE_USER', {
      targetEmail: email
    });
  } catch (error) {
    console.error('Error deleting authorized user:', error);
    throw error;
  }
};

// Get activity logs (super admin only)
export const getActivityLogs = async (limitCount: number = 100): Promise<ActivityLog[]> => {
  try {
    const logsQuery = query(
      collection(db, 'activityLog'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(logsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ActivityLog));
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
};

// Log user activity
export const logActivity = async (action: string, details: any): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user?.email) return;

    await setDoc(doc(collection(db, 'activityLog')), {
      userEmail: user.email,
      action,
      details,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error logging activity:', error);
    // Don't throw error for logging failures
  }
};

// Check if user is authorized
export const isUserAuthorized = async (email: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'authorizedUsers', email));
    return userDoc.exists() && userDoc.data()?.isActive === true;
  } catch (error) {
    console.error('Error checking user authorization:', error);
    return false;
  }
};

// Auto-create super admin if needed
export const ensureSuperAdmin = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user?.email || user.email !== 'aaron@saycoo.com') return;

    const userDoc = await getDoc(doc(db, 'authorizedUsers', user.email));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'authorizedUsers', user.email), {
        email: user.email,
        displayName: user.displayName || 'Aaron (Super Admin)',
        role: 'super_admin',
        isActive: true,
        createdAt: serverTimestamp(),
        createdBy: 'system',
        lastLoginAt: serverTimestamp()
      });
    } else {
      // Update last login
      await updateDoc(doc(db, 'authorizedUsers', user.email), {
        lastLoginAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error ensuring super admin:', error);
  }
};